import { FC, useCallback, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Button, useThemeValue } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog, useNotification } from '@/_shared/hooks';
import { putContentsRatingCall } from '@/training/contentsRating/state/api';

type Props = {
  id: string;
  onClose: () => void;
};

export const DialogFeedbackVideo: FC<Props> = ({ id, onClose }) => {
  const t = useTranslations('video.feedbackVideoDialog');
  const tShared = useTranslations();
  const [submited, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { closeDialog, Dialog, isOpen } = useDialog('feedbackVideoDialog');
  const { execute: executePutContentsRating, loading: loadingPutContentsRating } =
    putContentsRatingCall.hook();
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`);

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      videoRating: undefined,
      videoComment: '',
    },
  });

  const videoRating = watch('videoRating');
  const videoComment = watch('videoComment');

  const onExitConfirm = useCallback(() => {
    closeDialog();
    onClose();
  }, [closeDialog, onClose]);

  const onGoToTraining = useCallback(() => {
    closeDialog();
    onClose();
    navigate(Routes.TrainingHardSkills);
  }, [closeDialog, navigate, onClose]);

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    data => {
      const { videoRating: videoRatingValue, videoComment: videoCommentValue } = data;
      executePutContentsRating({
        contentId: id,
        score: videoRatingValue,
        comment: videoCommentValue,
        successSaga: () => {
          setSubmitted(true);
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    },
    [executePutContentsRating, id, notify, tShared],
  );

  return (
    <Dialog
      maxWidth='700px'
      open={isOpen}
      title={undefined}
      onClose={submited ? onGoToTraining : onExitConfirm}
      closeButton
    >
      <Box display='flex' justifyContent='flex-start' flexDirection='column' px='s' width='100%'>
        <Box display='flex' flexDirection='column'>
          <Typography variant='h3' mb='none' color='primary/text'>
            {t(!submited ? 'title' : 'submittedTitle')}
          </Typography>
        </Box>
        {!submited ? (
          <Box display='flex' mt='xxxs' flexDirection='column'>
            <Typography variant='p3/light' color='grey/light'>
              {t('description')}
            </Typography>
          </Box>
        ) : null}
        <Box mt='s'>
          {/* @ts-ignore */}
          <Form.FormRating control={control} name='videoRating' disabled={submited} />
        </Box>
        {videoRating && !submited ? (
          <Box width='100%' mt='s'>
            <Form.FormTextArea
              //  @ts-ignore
              control={control}
              name='videoComment'
              placeholder={t('commentPlaceholder')}
            />
          </Box>
        ) : null}
        {submited ? (
          <Box display='flex' mt='xxxs' flexDirection='column'>
            <Typography variant='p3/light' color='grey/light' fontStyle='italic'>
              {videoComment}
            </Typography>
          </Box>
        ) : null}
        {submited ? (
          <Box display='flex' mt='xs' flexDirection='column'>
            <Typography variant='p2'>{t('submittedDescription')}</Typography>
          </Box>
        ) : null}
      </Box>

      {videoRating ? (
        <Box
          display='flex'
          mx='l'
          flexDirection='row'
          px='s'
          style={{ gap: gapL }}
          justifyContent='flex-end'
          marginLeft='auto'
          mr={0}
        >
          <Button
            variant='primary'
            onClick={submited ? onGoToTraining : handleSubmit(onSubmit)}
            mt='l'
            loading={loadingPutContentsRating}
            spinnerColor='primary/text'
          >
            {submited ? t('backToTraining') : t('confirm')}
          </Button>
        </Box>
      ) : null}
    </Dialog>
  );
};
